import { createContext, FC, useContext, ReactNode, useEffect } from 'react';
import { useAuthStore } from '@/stores/AuthStore';
import { loadAdLibrary } from './loadAdLibrary';
import { useAdDataLayer } from './useAdDataLayer';

interface AdsContextValue {
  adsEnabled: boolean;
}

const AdsContext = createContext<AdsContextValue>({ adsEnabled: true });

// eslint-disable-next-line react-refresh/only-export-components
export const useAds = () => useContext(AdsContext);

/** Syncs window.nuk on every route change. Must live inside BrowserRouter. */
const AdDataLayerSync: FC<{ adsEnabled: boolean }> = ({ adsEnabled }) => {
  const userId = useAuthStore(s => s.userId);
  const isLoggedIn = useAuthStore(s => s.isReady && s.userId !== null);

  useAdDataLayer({
    blocked: !adsEnabled,
    cpn: userId ?? '',
    isLoggedIn,
  });

  useEffect(() => {
    if (!userId) return;
    window._sp_?.authenticated?.(userId);
  }, [userId]);

  useEffect(() => {
    if (!adsEnabled || typeof window === 'undefined') return;

    const timeoutId = window.setTimeout(() => {
      void loadAdLibrary().catch(error => {
        if (import.meta.env.DEV) {
          console.warn('Unable to load News UK ad library', error);
        }
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [adsEnabled]);

  return null;
};

interface AdProviderProps {
  children: ReactNode;
  adsEnabled?: boolean;
}

/**
 * Provides ad context and keeps window.nuk in sync with routing.
 * Place inside <BrowserRouter> so AdDataLayerSync can access useLocation.
 */
export const AdProvider: FC<AdProviderProps> = ({ children, adsEnabled = true }) => {
  return (
    <AdsContext.Provider value={{ adsEnabled }}>
      <AdDataLayerSync adsEnabled={adsEnabled} />
      {children}
    </AdsContext.Provider>
  );
};
